import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { KpiData } from '../data/mockSpaceYieldData'
import { AlertTriangle, DollarSign, Lightbulb, PieChart } from 'lucide-react'

interface KpiSectionProps {
  data: KpiData
  includeFuel: boolean
}

export const YieldKpiSection: React.FC<KpiSectionProps> = ({ data, includeFuel }) => {
  const formatCurrency = (val: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      
      {/* 1. Total Yield */}
      <Card className="hover:border-slate-400 cursor-pointer transition-colors shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600 uppercase">
            {includeFuel ? "Total Tenant Value" : "Total Yield"}
          </CardTitle>
          <DollarSign className="h-4 w-4 text-emerald-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatCurrency(includeFuel ? data.total_value : data.total_yield)}
            <span className="text-sm font-normal text-slate-500">/mo</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {includeFuel 
                ? `Rent: ${formatCurrency(data.total_yield)} · Fuel: ${formatCurrency(data.fuel_revenue)}`
                : `Lease: ${formatCurrency(data.lease_revenue)} · Trans: ${formatCurrency(data.transient_revenue)}`
            }
          </p>
          <p className="text-xs text-slate-400 mt-1">
            ({formatCurrency(data.yield_annualized)}/yr annualized)
          </p>
        </CardContent>
      </Card>

      {/* 2. Occupancy */}
      <Card className="hover:border-slate-400 cursor-pointer transition-colors shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">
            OCCUPANCY
          </CardTitle>
          <PieChart className="h-4 w-4 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.occupancy_pct}%</div>
          <p className="text-xs text-slate-500 mt-1">
            {data.producing_sqft.toLocaleString()} / {data.total_sqft.toLocaleString()} sqft producing
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {data.producing_units} of {data.total_units} units
          </p>
        </CardContent>
      </Card>

      {/* 3. Opportunity */}
      <Card className="hover:border-slate-400 cursor-pointer transition-colors shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">
            OPPORTUNITY
          </CardTitle>
          <Lightbulb className="h-4 w-4 text-amber-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">Est. {formatCurrency(data.opportunity_monthly)}<span className="text-sm font-normal text-slate-500">/mo</span></div>
          <p className="text-xs text-slate-500 mt-1">
            {data.idle_units} idle units · {data.underpriced_units} underpriced
          </p>
           <p className="text-xs text-slate-400 mt-1">
            ({formatCurrency(data.opportunity_annualized)}/yr potential)
          </p>
        </CardContent>
      </Card>

      {/* 4. Expiring */}
      <Card className="hover:border-slate-400 cursor-pointer transition-colors shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">
            EXPIRING (90 DAYS)
          </CardTitle>
          <AlertTriangle className="h-4 w-4 text-rose-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.expiring_count} fixed-term</div>
          <p className="text-xs text-slate-500 mt-1">
            {formatCurrency(data.expiring_risk)}/mo risk
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {data.m2m_count} month-to-month
          </p>
        </CardContent>
      </Card>

    </div>
  )
}
